import React, { useState, useRef } from "react";
import { Mail, CheckCircle } from "lucide-react";
import { cn } from "../../lib/utils";
import { SITE_CONFIG } from "../../lib/constants";
import { Button } from "../ui/Button";
import { createMailingList } from "../mailing_lists/shared";
import Recaptcha from "../shared/Recaptcha";

export function Subscribe({ className, siteKey, recaptchaEnabled }) {
  const recaptchaRef = useRef(null);
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const handleRecaptchaToken = (token) => {
    setRecaptchaToken(token);
    submitForm(token);
  };

  const submitForm = (token = null) => {
    if (email) {
      createMailingList(email, () => {
        setIsSubmitted(true);
      }, () => {
        setRecaptchaToken(null); // Reset token on failure so recaptcha re-executes on retry
      }, token);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (recaptchaEnabled) {
      if (!recaptchaToken) {
        recaptchaRef.current.execute();
        return;
      }
    }

    submitForm(recaptchaToken);
  };

  return (
    <section
      className={cn(
        "py-12 md:py-16 px-4",
        "bg-ink relative overflow-hidden",
        className
      )}
    >
      {/* Decorative background elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Geometric pattern */}
        <div className="absolute top-0 left-0 w-32 h-32 border border-gold/10 rotate-45 -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute top-0 right-0 w-48 h-48 border border-gold/10 rotate-45 translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-1/4 w-24 h-24 border border-gold/10 rotate-45 translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-40 h-40 border border-gold/10 rotate-45 translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="relative max-w-2xl mx-auto text-center">
        {/* Icon */}
        <div className="scroll-animate mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/20 mb-4">
            <Mail className="w-8 h-8 text-gold" />
          </div>
        </div>

        {/* Heading */}
        <div className="scroll-animate">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-paper! mb-4">
            Join The Brief
          </h2>
          <p className="font-body text-lg text-paper/80 mb-8 max-w-md mx-auto">
            Get essential mining intelligence delivered to your inbox every
            week.
          </p>
        </div>

        {/* Form */}
        <div className="scroll-animate">
          {isSubmitted ? (
            <div className="inline-flex flex-col items-center gap-4 p-8 bg-paper/10 rounded-lg border border-gold/30">
              <CheckCircle className="w-12 h-12 text-gold" />
              <div>
                <p className="font-serif text-xl text-paper font-semibold mb-2">
                  You're on the list!
                </p>
                <p className="font-body text-paper/70">
                  Check your inbox for a welcome email from{" "}
                  {SITE_CONFIG.name}.
                </p>
              </div>
            </div>
          ) : (
            <>
              <form
                onSubmit={handleSubmit}
                className="flex flex-col sm:flex-row items-center justify-center gap-4"
              >
                <div className="w-full sm:w-80">
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={cn(
                      "w-full h-12 px-4",
                      "font-body text-ink text-base",
                      "bg-paper",
                      "border-2 border-gold/50",
                      "focus:outline-none focus:border-gold",
                      "placeholder:text-ink-muted",
                      "transition-colors duration-300"
                    )}
                  />
                </div>
                <Button type="submit" variant="primary" size="lg" onClick={handleSubmit}>
                  Subscribe Free
                </Button>
              </form>
              {recaptchaEnabled && (
                <Recaptcha
                  ref={recaptchaRef}
                  siteKey={siteKey}
                  theme="light"
                  onToken={handleRecaptchaToken}
                />
              )}

              <p className="mt-4 font-sans text-sm text-paper/50">
                No spam. Unsubscribe anytime. Privacy respected.
              </p>
            </>
          )}
        </div>

        {/* Decorative bottom element */}
        <div className="mt-12 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-gold rotate-45" />
            <div className="w-12 h-px bg-gold/30" />
            <div className="w-3 h-3 border border-gold rotate-45" />
            <div className="w-12 h-px bg-gold/30" />
            <div className="w-2 h-2 bg-gold rotate-45" />
          </div>
        </div>
      </div>
    </section>
  );
}
